/**
 * Sign out (PRD §11.4).
 *
 * Offers both behaviours the PRD requires: ending this browser's session, or a
 * global logout that invalidates every session and its refresh tokens. Because
 * global logout is not undoable, it is an explicit second choice rather than a
 * checkbox someone can miss.
 */
import { useCallback, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { AuthLayout } from '@/components/auth-layout';
import { DarkAlert, DarkButton, DarkSubmit } from '@/components/ui';
import { useAuth } from '@/hooks/use-auth';

export function SignOutPage() {
  const { user, loading, logout } = useAuth();
  const [pending, setPending] = useState<'local' | 'global' | null>(null);
  const [signedOut, setSignedOut] = useState(false);

  const run = useCallback(
    (scope: 'local' | 'global') => {
      setPending(scope);
      void (async () => {
        try {
          await logout(scope === 'global');
          setSignedOut(true);
        } finally {
          setPending(null);
        }
      })();
    },
    [logout],
  );

  if (signedOut) {
    return (
      <AuthLayout title="You're signed out" description="Thanks for using MindPeer.">
        <div className="space-y-5">
          <DarkAlert tone="success">Your session has ended.</DarkAlert>
          <Link to="/sign-in">
            <DarkSubmit type="button">Sign in again</DarkSubmit>
          </Link>
        </div>
      </AuthLayout>
    );
  }

  // Arriving here without a session is not an error — just show the exit state.
  if (!loading && !user) return <Navigate to="/sign-in" replace />;

  return (
    <AuthLayout title="Sign out" description="Choose how far this should reach.">
      <div className="space-y-5">
        <div className="space-y-2">
          <DarkSubmit
            type="button"
            loading={pending === 'local'}
            disabled={pending !== null}
            onClick={() => run('local')}
          >
            Sign out of this browser
          </DarkSubmit>
          <p className="text-sm text-slate-400">
            Ends this session only. Your other devices stay signed in.
          </p>
        </div>

        <div className="space-y-2 border-t border-white/10 pt-5">
          <DarkButton
            tone="danger"
            loading={pending === 'global'}
            disabled={pending !== null}
            onClick={() => run('global')}
          >
            Sign out everywhere
          </DarkButton>
          <p className="text-sm text-slate-400">
            Ends every MindPeer session on every device. You&apos;ll need to sign in again before
            opening any module.
          </p>
        </div>

        <div className="border-t border-white/10 pt-5 text-center">
          <Link to="/account" className="text-sm font-medium text-brand-400 hover:text-brand-300">
            Stay signed in
          </Link>
        </div>
      </div>
    </AuthLayout>
  );
}
